import { useAtom } from "jotai";
import Axios from "axios";
import { useCallback, useMemo, useRef } from "react";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import { IMockupCategory, mockupCategoryAtom } from "./types";

type SetMockupCatoryType = (payload: IMockupCategory[]) => void;
type FetchMockupCategories = () => void;

type MockupCategoryKeyFunctions = {
    get: FetchMockupCategories
}

type UseMockupCategories = [
    IMockupCategory[],
    SetMockupCatoryType,
    MockupCategoryKeyFunctions
];

export const useMockupCategories = (): UseMockupCategories => {
    const [mockupCategories, setMockupCategories] = useAtom(mockupCategoryAtom);

    const [,setSnackbar] = useSnackbar();
    const [,authToken, loginFunctions] = useLogin();
    const hasFetchedRef = useRef(false);
    const isFetchingRef = useRef(false);

    const getMockupCategories = useCallback(() => {
        if (!authToken || hasFetchedRef.current || isFetchingRef.current) return;
        if (mockupCategories.length > 0) {
            hasFetchedRef.current = true;
            return;
        }
        const fetchCategories = async (setter: SetMockupCatoryType): Promise<void> => {
            isFetchingRef.current = true;
            try {
                const result = await Axios.get(environment.mockupCategoriesUrl, {
                    headers: {
                        "X-Wallmates-Auth": authToken
                    }
                }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
                if (result) {
                    setter(result.data);
                    hasFetchedRef.current = true;
                } else {
                    setSnackbar({
                        show: true,
                        snackbarLevel: "error",
                        text: "There was an error while getting mockup categories. Please try again later."
                    });
                }
            } finally {
                isFetchingRef.current = false;
            }
        };
        fetchCategories(setMockupCategories);
    }, [authToken, loginFunctions.logout, mockupCategories.length, setMockupCategories, setSnackbar]);

    const keyFunctions: MockupCategoryKeyFunctions = useMemo(() => ({
        get: getMockupCategories
    }), [getMockupCategories]);

    return [
        mockupCategories,
        setMockupCategories,
        keyFunctions
    ]
}